import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

// @ts-expect-error
import capitalize from 'underscore.string/capitalize';

import {Card} from 'shells/card/card';
import {Stack} from 'shells/layout/stack/stack';
import {Link} from 'shells/link/link';
import {PhoneMessagePreview} from 'shells/sms-campaigns/message-preview/phone-message-preview';
import {Body} from 'shells/typography/body';

import {CollapsibleSmsStats} from '../../../audiences/membership/collapsible-sms-stats';
import type {SelectedRecipients} from '../../../editions/prepare-to-send-page/new-recipients-selection-view';

import styles from './review-and-send.module.css';

type Props = {
    name: string;
    from: string | null | undefined;
    content: string;
    optOutText: string | null | undefined;
    userFacingNoun: string;
    selectedRecipientList: SelectedRecipients | null | undefined;
};

export function ReviewAndSend(props: Props) {
    const selectedAudience =
        props.selectedRecipientList?.type === 'audience'
            ? props.selectedRecipientList.recipientSelection
            : undefined;

    return (
        <div className={styles.container}>
            <div className={styles.left}>
                <PhoneMessagePreview
                    message={props.content}
                    optOutText={props.optOutText || undefined}
                />
            </div>
            <div className='pad-16'>
                <Card hasBorder={true}>
                    <div className='pad-16'>
                        <Stack spacing={24}>
                            <HeaderAndContent
                                header={capitalize(props.userFacingNoun)}
                                content={props.name}
                            />
                            <HeaderAndContent
                                header='From'
                                content={
                                    props.from ? (
                                        props.from
                                    ) : (
                                        <Link
                                            // @ts-expect-error
                                            as={RoutedLink}
                                            variant='primary'
                                            to='/inbox/settings/sms/enroll'
                                        >
                                            Configure your phone number
                                        </Link>
                                    )
                                }
                            />
                            <HeaderAndContent
                                header='To'
                                content={
                                    props.selectedRecipientList &&
                                    props.selectedRecipientList.recipientSelection ? (
                                        <Stack spacing={4}>
                                            <Link
                                                variant='primary'
                                                href={
                                                    props.selectedRecipientList.recipientSelection
                                                        .htmlUrlPath
                                                }
                                                newTab={true}
                                            >
                                                {
                                                    props.selectedRecipientList.recipientSelection
                                                        .name
                                                }
                                            </Link>
                                            {selectedAudience &&
                                            selectedAudience.smsMembershipStats &&
                                            NutClientConfig.hasSmsAudiencesFF ? (
                                                <>
                                                    <Body size='small'>
                                                        {
                                                            selectedAudience.smsMembershipStats
                                                                .totalCount.value
                                                        }{' '}
                                                        {selectedAudience.smsMembershipStats
                                                            .totalCount.value === 1
                                                            ? 'member'
                                                            : 'members'}
                                                    </Body>
                                                    <Card hasBorder={true} borderRadius={8}>
                                                        <CollapsibleSmsStats
                                                            audienceId={selectedAudience.id}
                                                            isCollapsed={true}
                                                            showChevron={true}
                                                        />
                                                    </Card>
                                                </>
                                            ) : (
                                                <Body size='small'>
                                                    {
                                                        props.selectedRecipientList
                                                            .recipientSelection.recipientCount
                                                    }{' '}
                                                    {props.selectedRecipientList.recipientSelection
                                                        .recipientCount === 1
                                                        ? 'recipient'
                                                        : 'recipients'}
                                                </Body>
                                            )}
                                        </Stack>
                                    ) : (
                                        <></>
                                    )
                                }
                            />
                        </Stack>
                    </div>
                </Card>
            </div>
        </div>
    );
}

const HeaderAndContent = (props: {header: string; content: string | React.ReactNode}) => {
    const {header, content} = props;
    const contentComponent =
        typeof content === 'string' ? <Body as='div'>{content}</Body> : content;

    return (
        <Stack spacing={4}>
            <Body isBold={true} as='div'>
                {header}
            </Body>
            <div className={styles['text-message-style']}>{contentComponent}</div>
        </Stack>
    );
};
